import React, { useState, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../styles/datepicker-theme.css';
import { useTheme } from '../../context/ThemeContext';
import { useAuthContext } from '../../context/AuthContext';
import { ymdToDate, dateToYmd } from '../../utils/dateInput';

const toCents = (value: number) => Math.round((value + Number.EPSILON) * 100);
const fromCents = (cents: number) => cents / 100;
const MAX_INSTALLMENTS = 52; // the custom scheduler's per-plan charge cap (mirrors LinkPayment)
const getMinScheduledAmountCents = (totalCents: number) => {
  if (totalCents <= 0) return 0;
  const tenDollarsCents = 1000;
  // Flat $10 minimum for any real bill; bills under $10 must be paid in full (100%).
  return Math.min(totalCents, tenDollarsCents);
};

// Suggested/default installment pre-filled into the amount box: 10% of the balance, but
// never below the $10 floor and never above the balance. So bills over $100 default to 10%;
// $10–$100 bills default to $10; sub-$10 bills default to the full balance. The patient can
// then drag this down to the $10 floor (getMinScheduledAmountCents) or raise it.
const getSuggestedAmountCents = (totalCents: number) => {
  if (totalCents <= 0) return 0;
  const tenDollarsCents = 1000;
  const tenPercentCents = Math.ceil(totalCents * 0.1);
  return Math.min(totalCents, Math.max(tenDollarsCents, tenPercentCents));
};

interface Payment {
  id: string;
  amount: number;
  totalDueAmount: number;
  status: string;
  dueDate: string;
  invoiceNumber?: string;
  description?: string;
  payabliToken?: string;
  payabliEntryId?: string;
  currency?: string;
  paymentType?: string;
  createdAt?: string;
  updatedAt?: string;
  statementDate?: string;
  paymentDate?: string;
  paymentIdentity?: {
    id: string;
    email: string;
    phone: string | null;
    firstName: string;
    lastName: string;
    uniqueId: string;
    guarantorLastName: string;
  };
}

interface UserInfo {
  name: string;
  gn: string;
  email?: string;
  phone?: string;
  guarantorLastName?: string;
}

interface UserInfoHeaderProps {
  userInfo: UserInfo;
  pendingPaymentsCount: number;
  payments?: Payment[];
  onPayNow?: (amount: number, isScheduled: boolean) => void;
  onLoadPayabliScript?: (amount: number, isScheduled: boolean, scheduledPaymentData?: any) => void;
  isNewFlow?: boolean;
  // Scheduled payment props
  scheduleFrequency?: string;
  setScheduleFrequency?: (value: string) => void;
  startDate?: string;
  setStartDate?: (value: string) => void;
  amountPerPayment?: string;
  setAmountPerPayment?: (value: string) => void;
  endDate?: string;
  isScheduledPaymentValid?: () => boolean;
  getMinStartDate?: () => string;
  SCHEDULE_FREQUENCIES?: any;
  pendingPaymentAmount?: number;
  /**
   * Hide the pay CTA entirely (amount input + Pay/Schedule button).
   *
   * Set for a Cerner account: the public capture endpoint allocates Meditech-only and SF-Backend#772
   * now refuses a Cerner guarantor, so this button could only ever dead-end. Gating the payment MODAL
   * alone was not enough -- this component owns the REAL CTA, so the button stayed live, opened
   * nothing, and sat directly above a panel telling the patient payment was unavailable.
   */
  paymentDisabled?: boolean;
}

const PAYMENT_TYPE_MAP = {
  scheduled: 'RECURRING',
  oneTime: 'ONE_TIME'
} as const;

const UserInfoHeader: React.FC<UserInfoHeaderProps> = ({
  userInfo,
  pendingPaymentsCount,
  payments = [],
  onPayNow,
  onLoadPayabliScript,
  isNewFlow = false,
  scheduleFrequency = 'weekly',
  setScheduleFrequency = () => {},
  startDate = '',
  setStartDate = () => {},
  amountPerPayment = '',
  setAmountPerPayment = () => {},
  endDate = '',
  isScheduledPaymentValid = () => true,
  getMinStartDate = () => new Date().toISOString().split('T')[0],
  SCHEDULE_FREQUENCIES = { WEEKLY: 'weekly', BIWEEKLY: 'biweekly', MONTHLY: 'monthly', QUARTERLY: 'quarterly' },
  pendingPaymentAmount = 0,
  paymentDisabled = false
}) => {
  const { theme } = useTheme();
  const { isAuthenticated } = useAuthContext();
  const [amount, setAmount] = useState<number>(0);
  const [displayValue, setDisplayValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isScheduledPayment, setIsScheduledPayment] = useState<boolean>(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [startDateError, setStartDateError] = useState<string>('');
  const [isStartDateValid, setIsStartDateValid] = useState<boolean>(true);

  // Calculate total amount from all payments
  const rawTotalAmount = payments.reduce((sum: number, payment: Payment) => sum + (payment?.totalDueAmount || 0), 0);
  const totalAmount = fromCents(toCents(rawTotalAmount));
  const totalAmountCents = toCents(totalAmount);

  const minScheduledAmountCents = getMinScheduledAmountCents(totalAmountCents);
  const minScheduledAmount = fromCents(minScheduledAmountCents);

  // Installments-first: the amount field IS the per-charge installment; the number of charges is
  // DERIVED so the plan pays off the full balance (the final charge is the remainder). Display-only —
  // the backend derives N and caps the final charge to the exact remainder (ISS-34).
  const perChargeCents = toCents(amount);
  const scheduledInstallmentCount = perChargeCents > 0 ? Math.ceil(totalAmountCents / perChargeCents) : 0;
  const scheduledFinalPayment = scheduledInstallmentCount > 0
    ? fromCents(totalAmountCents - (scheduledInstallmentCount - 1) * perChargeCents)
    : 0;

  const getSuggestedAmount = useCallback((total: number) => {
    const totalCents = toCents(total);
    if (totalCents <= 0) return 0;
    return fromCents(getSuggestedAmountCents(totalCents));
  }, []);

  // Validate start date
  const validateStartDate = useCallback((date: string) => {
    if (!date) {
      setStartDateError('');
      setIsStartDateValid(true);
      return true;
    }

    const selectedDate = ymdToDate(date);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 31);
    maxDate.setHours(0, 0, 0, 0);

    if (!selectedDate || selectedDate < tomorrow) {
      setStartDateError('Start date must be at least tomorrow');
      setIsStartDateValid(false);
      return false;
    }

    if (selectedDate > maxDate) {
      setStartDateError('Start date cannot be more than 30 days from tomorrow');
      setIsStartDateValid(false);
      return false;
    }

    setStartDateError('');
    setIsStartDateValid(true);
    return true;
  }, []);

  // Reset amount when payments change or when component mounts
  useEffect(() => {
    if (totalAmount > 0 && amount === 0) {
      const suggested = getSuggestedAmount(totalAmount);
      setAmount(suggested);
      if (!isFocused) setDisplayValue(suggested ? suggested.toFixed(2) : '');
    }
  }, [totalAmount, amount, getSuggestedAmount, isFocused]);

  // Validate start date when scheduled payment is toggled or start date changes
  useEffect(() => {
    if (isScheduledPayment && startDate) {
      validateStartDate(startDate);
    } else {
      setStartDateError('');
      setIsStartDateValid(true);
    }
  }, [isScheduledPayment, startDate, validateStartDate]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Allow empty string, numbers, and decimal point
    if (inputValue === '' || /^\d*\.?\d*$/.test(inputValue)) {
      setDisplayValue(inputValue);
      const value = parseFloat(inputValue);
      const newAmount = isNaN(value) ? 0 : value;
      setAmount(newAmount);
    }
  };

  // Handle key down events to prevent invalid input and manage cursor position
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    
    // Prevent down arrow from making value negative when field is empty or has 0
    if (e.key === 'ArrowDown' && (input.value === '' || input.value === '0' || input.value === '0.00')) {
      e.preventDefault();
    }
    
    // Handle cursor positioning when value becomes 0.00
    if (e.key === 'Backspace' && input.value === '0.00') {
      // Set cursor to beginning of input
      setTimeout(() => {
        input.setSelectionRange(0, 0);
      }, 0);
    }
    
    // Prevent invalid characters
    if (e.key === '-' || e.key === '+') {
      e.preventDefault();
    }
  };

  // Handle focus events
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    // Show raw value when focused (without .00 formatting)
    if (amount > 0) {
      const roundedAmount = fromCents(toCents(amount));
      setDisplayValue(roundedAmount.toString());
    } else {
      setDisplayValue('');
    }
  };

  // Handle blur events - format the value when focus is lost
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    // Format the value when focus is lost
    if (amount > 0) {
      const roundedAmount = fromCents(toCents(amount));
      setDisplayValue(roundedAmount.toFixed(2));
    } else {
      setDisplayValue('');
    }
  };


  const handleScheduledPaymentChange = (checked: boolean) => {
    if (checked && !isAuthenticated) {
      setShowLoginPopup(true);
    } else {
      setIsScheduledPayment(checked);
      // Switching to scheduled: if the current amount is below the $10 floor, snap it back to
      // the suggested default (10% / greater-of rule), same as the initial pre-fill.
      if (checked && toCents(amount) < minScheduledAmountCents) {
        const suggested = getSuggestedAmount(totalAmount);
        setAmount(suggested);
        if (!isFocused) setDisplayValue(suggested ? suggested.toFixed(2) : '');
      }
    }
  };

  const closeLoginPopup = () => {
    setShowLoginPopup(false);
  };

  const handleLoginRedirect = () => {
    // Save current URL to localStorage
    const currentUrl = window.location.pathname + window.location.search;
    localStorage.setItem('redirectAfterLogin', currentUrl);
    
    // Redirect to login page
    window.location.href = '/login';
    closeLoginPopup();
  };

  // Local time throughout, matching the dateInput helper the picker + validation use, so the whole
  // date story is consistent (no UTC/local boundary-day edge in an odd timezone).
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return dateToYmd(tomorrow);
  };

  // 30 days from tomorrow (local)
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 31); // 30 days from tomorrow
    return dateToYmd(maxDate);
  };

  const isAmountValid = () => {
    const amountCents = toCents(amount);

    if (amountCents <= 0) {
      return false;
    }
    
    // Check if amount exceeds total amount
    if (amountCents > totalAmountCents) {
      return false;
    }
    
    if (isScheduledPayment) {
      // Also require the DERIVED charge count to be within the scheduler's 1..MAX cap, so a per-charge
      // that would need >MAX installments disables the button up front instead of erroring on submit.
      return amountCents >= minScheduledAmountCents && isScheduledPaymentValid() && isStartDateValid
        && scheduledInstallmentCount >= 1 && scheduledInstallmentCount <= MAX_INSTALLMENTS;
    }
    return amountCents > 0 && amountCents <= totalAmountCents;
  };

  const handlePayNow = () => {
    if (!isAmountValid()) return;

    // Use total amount if no specific amount is entered
    const finalAmount = amount > 0 ? amount : totalAmount;

  // Prepare scheduled payment data
    const scheduledPaymentData = isScheduledPayment ? {
      isScheduled: true,
      // Keep UI frequency (weekly/biweekly/monthly/quarterly) in state;
      // the parent maps it to API format when submitting.
      frequency: scheduleFrequency,
      paymentType: PAYMENT_TYPE_MAP.scheduled,
      startDate: startDate,
      endDate: endDate,
      amountPerPayment: amountPerPayment,
    } : {
      isScheduled: false,
      frequency: 'ONETIME',
      paymentType: PAYMENT_TYPE_MAP.oneTime,
    };

    if (isNewFlow && onLoadPayabliScript) {
      // New flow: Use paymentIdentityId and open Payabali popup
      onLoadPayabliScript(finalAmount, isScheduledPayment, scheduledPaymentData);
    } else if (onPayNow) {
      // Old flow: Use existing onPayNow callback
      onPayNow(finalAmount, isScheduledPayment);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 mb-6 w-full">
      <div className="flex flex-col w-full">
        <div className="flex-1 w-full">
          {/* Total Amount Display */}
          <div className="mb-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-slate-800 mb-2">Payment Summary</h2>
              <p className="text-slate-600 text-sm">
                Review your total amount and choose your payment option
              </p>
            </div>
            <div
              className={`rounded-lg px-8 py-6 text-center border ${
                theme.client === 'stluke' ? 'bg-[#F2F5F9] border-slate-200' : 'bg-cyan-50 border-cyan-100'
              }`}
              data-testid="total-amount-display"
            >
              <div className={`text-sm font-medium mb-2 uppercase tracking-wide ${theme.client === 'stluke' ? theme['text-600'] : 'text-cyan-600'}`}>
                Total Amount Due
              </div>
              <div className={`text-3xl font-bold ${theme.client === 'stluke' ? theme['text-default-800'] : 'text-cyan-900'}`}>
                {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalAmount || 0)}
              </div>
              {pendingPaymentAmount > 0 && (
                <div className="text-sm text-amber-600 mt-1">
                  {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(pendingPaymentAmount)} in payments pending processing
                </div>
              )}
            </div>
          </div>
          
          {/* Enhanced Scheduled Payment Option */}
          <div className="mb-6">
            <div
              className={`border rounded-lg p-4 transition-colors duration-200 ${
                theme.client === 'stluke'
                  ? 'bg-white border-slate-200 hover:bg-[#F2F5F9]'
                  : 'bg-lime-50 border-lime-200 hover:bg-lime-100'
              }`}
            >
              <label className="scheduled-payment-checkbox flex items-center gap-3 cursor-pointer">
                <div className="flex-shrink-0">
                  <input
                    type="checkbox"
                    data-testid="scheduled-payment-checkbox"
                    checked={isScheduledPayment}
                    onChange={(e) => handleScheduledPaymentChange(e.target.checked)}
                    className={`w-4 h-4 rounded focus:ring-2 ${
                      theme.client === 'stluke'
                        ? 'text-[#71001F] border-[#71001F] focus:ring-[#71001F]'
                        : 'text-lime-500 border-lime-300 focus:ring-lime-400'
                    }`}
                  />
                </div>
                <div className="flex flex-col">
                  <div className="mb-1">
                    <span className={`text-base font-semibold ${theme.client === 'stluke' ? theme['text-default-800'] : 'text-lime-800'}`}>
                      Set up scheduled payments
                    </span>
                  </div>
                  <span className={`text-sm ${theme.client === 'stluke' ? 'text-slate-600' : 'text-lime-600'}`}>
                    Automate your payments and never miss a due date
                  </span>
                </div>
              </label>
            </div>
          </div>

          {/* Enhanced Scheduled Payment Form */}
          {isScheduledPayment && (
            <div className="scheduled-payment-form bg-amber-50 border border-amber-200 rounded-lg p-5 mb-6 payment-form-animate">
              <div className="mb-4">
                <h3 className="text-base font-semibold text-amber-800 mb-1">
                  Payment Schedule Configuration
                </h3>
                <p className="text-amber-600 text-sm">Customize your payment schedule preferences</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Frequency Selection */}
                <div>
                  <label className="block text-sm font-medium text-amber-700 mb-2">
                    Payment Frequency
                  </label>
                  <select
                    value={scheduleFrequency}
                    onChange={(e) => setScheduleFrequency(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-amber-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 bg-white transition-colors duration-200"
                  >
                    <option value={SCHEDULE_FREQUENCIES.WEEKLY}>Weekly</option>
                    <option value={SCHEDULE_FREQUENCIES.MONTHLY}>Monthly</option>
                    <option value={SCHEDULE_FREQUENCIES.QUARTERLY}>Quarterly</option>
                  </select>
                </div>

                {/* Start Date */}
                <div>
                  <label className="block text-sm font-medium text-amber-700 mb-2">
                    Start Date
                  </label>
                  <DatePicker
                    selected={ymdToDate(startDate)}
                    onChange={(d: Date | null) => {
                      const s = dateToYmd(d);
                      setStartDate(s);
                      validateStartDate(s);
                    }}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="mm/dd/yyyy"
                    minDate={ymdToDate(getTomorrowDate()) ?? undefined}
                    maxDate={ymdToDate(getMaxDate()) ?? undefined}
                    calendarClassName={theme.client === 'stluke' ? 'stluke-datepicker' : undefined}
                    popperClassName={theme.client === 'stluke' ? 'stluke-datepicker-popper' : undefined}
                    wrapperClassName="w-full"
                    id="start-date-input"
                    required
                    className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 bg-white transition-colors duration-200 ${
                      theme.client === 'stluke'
                        ? (startDateError ? 'border-yellow-300 focus:ring-[#71001F]' : 'border-slate-300 focus:ring-[#71001F]')
                        : (startDateError ? 'border-yellow-300 focus:border-yellow-500 focus:ring-amber-400' : 'border-amber-300 focus:border-amber-400 focus:ring-amber-400')
                    }`}
                  />
                  {startDateError && (
                    <p className="text-yellow-600 text-xs mt-1 font-medium">
                      {startDateError}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Amount Input — suppressed entirely when payment is unavailable for this EHR. */}
          {!paymentDisabled && (
          <div
            className={`rounded-lg p-6 border ${
              theme.client === 'stluke' ? 'bg-white border-slate-200' : 'bg-violet-50 border-violet-200'
            }`}
          >
            <div className="mb-6 text-center">
              <h3 className={`text-lg font-semibold mb-2 ${theme.client === 'stluke' ? theme['text-default-800'] : 'text-violet-800'}`}>
                Ready to Make Your Payment?
              </h3>
              <p className={`text-sm ${theme.client === 'stluke' ? 'text-slate-600' : 'text-violet-600'}`}>
                Enter your payment amount and complete your transaction
              </p>
            </div>
            
            <div className="max-w-2xl mx-auto">
              <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-4">
                <div className="flex-1">
                  <label className={`block text-sm font-medium mb-2 ${theme.client === 'stluke' ? 'text-slate-700' : 'text-violet-700'}`}>{isScheduledPayment ? 'Amount per payment' : 'Payment Amount'}</label>
                  <input
                    value={displayValue}
                    type="text"
                    inputMode="decimal"
                    onChange={handleAmountChange}
                    onKeyDown={handleKeyDown}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    className={`w-full px-4 py-3 text-base border rounded-lg focus:outline-none focus:ring-2 bg-white transition-colors duration-200 ${
                      theme.client === 'stluke'
                        ? 'border-slate-300 focus:ring-[#71001F] focus:border-[#71001F]'
                        : 'border-violet-300 focus:ring-violet-400 focus:border-violet-400'
                    }`}
                    placeholder={`Enter amount (max: $${totalAmount.toFixed(2)})`}
                    data-testid="payment-amount-input"
                  />
                </div>
                <div className="lg:w-auto">
                  <label className={`block text-sm font-medium mb-2 lg:invisible ${theme.client === 'stluke' ? 'text-slate-700' : 'text-violet-700'}`}>Action</label>
                  <button
                    className={`w-full lg:w-auto px-8 py-3 rounded-lg text-white text-base font-semibold transition-colors duration-200 whitespace-nowrap ${
                      isAmountValid() 
                        ? `${theme["button-bg"]} hover:opacity-90` 
                        : 'bg-slate-400 cursor-not-allowed'
                    }`}
                    onClick={handlePayNow}
                    disabled={!isAmountValid()}
                    data-testid="pay-now-button"
                  >
                    {isScheduledPayment ? 'Schedule Payment' : `Pay ${amount > 0 ? `$${amount.toFixed(2)}` : `$${totalAmount.toFixed(2)}`}`}
                  </button>
                </div>
              </div>
            </div>
            <div className="mt-4 max-w-2xl mx-auto">
              {isScheduledPayment && toCents(amount) < minScheduledAmountCents && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-yellow-600 text-sm font-medium">
                    Scheduled payments require a minimum of ${minScheduledAmount.toFixed(2)} ($10, or your full balance if it's under $10)
                  </p>
                </div>
              )}
              {isScheduledPayment && amount > 0 && toCents(amount) >= minScheduledAmountCents && toCents(amount) <= totalAmountCents && scheduledInstallmentCount >= 1 && scheduledInstallmentCount <= MAX_INSTALLMENTS && (
                <div className={`mt-2 rounded-lg p-3 ${theme.client === 'stluke' ? 'bg-[#F2F5F9] border border-slate-200' : 'bg-blue-50 border border-blue-100'}`}>
                  <p className="text-sm text-slate-700">
                    This schedules{' '}
                    <span className="font-semibold">{scheduledInstallmentCount} payment{scheduledInstallmentCount === 1 ? '' : 's'}</span>{' '}
                    of <span className="font-semibold">${amount.toFixed(2)}</span>{' '}
                    to pay off your <span className="font-semibold">${totalAmount.toFixed(2)}</span> balance
                    {scheduledInstallmentCount > 1 && toCents(scheduledFinalPayment) !== perChargeCents ? (
                      <> — final payment <span className="font-semibold">${scheduledFinalPayment.toFixed(2)}</span>.</>
                    ) : '.'}
                  </p>
                </div>
              )}
              {isScheduledPayment && toCents(amount) >= minScheduledAmountCents && toCents(amount) <= totalAmountCents && scheduledInstallmentCount > MAX_INSTALLMENTS && (
                <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-yellow-600 text-sm font-medium">
                    That amount per payment would need {scheduledInstallmentCount} charges (max {MAX_INSTALLMENTS}). Please enter a larger amount per payment.
                  </p>
                </div>
              )}
              {toCents(amount) > totalAmountCents && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-yellow-600 text-sm font-medium">
                    Payment amount cannot exceed total amount due
                  </p>
                </div>
              )}
            </div>
          </div>
          )}

          

          {/* Login Popup */}
          {showLoginPopup && (
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
              <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
                <h2 className="text-lg sm:text-xl font-bold mb-4">Login Required</h2>
                <p className="mb-6 text-sm sm:text-base">You need to login to schedule payments.</p>
                <div className="flex justify-end space-x-3">
                  <button 
                    onClick={closeLoginPopup}
                    className="px-3 sm:px-4 py-2 border border-gray-300 rounded-md text-sm sm:text-base text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleLoginRedirect}
                    className={`px-3 sm:px-4 py-2 rounded-md text-white text-sm sm:text-base ${theme["button-bg"]}`}
                  >
                    Login
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* <div className="text-right ml-6">
          <p className="text-sm text-gray-500">Total Pending Payments</p>
          <p className={`text-2xl font-bold ${theme["text-600"]}`}>{pendingPaymentsCount}</p>
        </div> */}
      </div>
    </div>
  );
};

export default UserInfoHeader; 